import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  Home, ShoppingBag, Building2, Car, TrendingUp, 
  PiggyBank, Landmark, Wallet, User, LogOut, Menu, X 
} from 'lucide-react';
import { useState } from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aimarketplace-18/artifacts/7akyhfla_IMG_5649.jpg';

const navItems = [
  { href: '/dashboard', icon: Home, label: 'Dashboard' },
  { href: '/buy', icon: ShoppingBag, label: 'Shop' },
  { href: '/homes', icon: Building2, label: 'Homes' },
  { href: '/auto', icon: Car, label: 'Auto' },
  { href: '/markets', icon: TrendingUp, label: 'Markets' },
  { href: '/wealth', icon: PiggyBank, label: 'Wealth' },
  { href: '/capital', icon: Landmark, label: 'Capital' },
  { href: '/wallet', icon: Wallet, label: 'Wallet' },
];

export const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside 
      className={cn(
        "hidden lg:flex flex-col h-screen bg-card border-r border-border fixed left-0 top-0 z-40 transition-all duration-300",
        collapsed ? "w-20" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Jambo" className="h-10 w-auto" />
          </Link>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto"
        >
          {collapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.href ||
            (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
          
          return (
            <Link
              key={item.href}
              to={item.href}
              data-testid={`sidebar-${item.label.toLowerCase()}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200",
                isActive 
                  ? "jambo-gradient text-white shadow-md" 
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-border">
        {!collapsed ? (
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-green-500 flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.phone}</p>
            </div>
          </div>
        ) : null}
        <Button 
          variant="ghost" 
          className={cn("w-full justify-start gap-3 text-red-500 hover:text-red-600 hover:bg-red-50", collapsed && "justify-center")}
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && "Logout"}
        </Button>
      </div>
    </aside>
  );
};
